import {
  computeSupportStats,
  downloadTransactionsCsv,
  formatMoney,
  toAmount,
  transactionTypeLabel,
  type SupportTransaction,
} from "@/lib/support";

const StatCard = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="bg-card rounded-2xl p-5 shadow-card">
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-display text-3xl mt-1.5">{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

type Props = {
  rows: SupportTransaction[];
  loading?: boolean;
  csvFilename?: string;
  showEmail?: boolean;
  emptyLabel?: string;
};

const TransactionsPanel = ({ rows, loading, csvFilename = "support-transactions.csv", showEmail = false, emptyLabel }: Props) => {
  const stats = computeSupportStats(rows);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
        <StatCard label={"Season Support"} value={formatMoney(stats.thisYear, stats.currency)} />
        <StatCard label="Total support received" value={formatMoney(stats.total, stats.currency)} />
        <StatCard label="Supporters" value={String(stats.supporters)} />
        <StatCard label="Average contribution" value={formatMoney(stats.average, stats.currency)} />
        <StatCard label="This month" value={formatMoney(stats.thisMonth, stats.currency)} />
      </div>

      <div className="bg-card rounded-2xl shadow-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-border">
          <div>
            <p className="font-display text-xl">Recent transactions</p>
            <p className="text-xs text-muted-foreground mt-0.5">Your own records for bookkeeping. Not tax advice.</p>
          </div>
          <button
            type="button"
            onClick={() => downloadTransactionsCsv(rows, csvFilename, showEmail)}
            disabled={rows.length === 0}
            className="px-5 py-2.5 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
          >
            Export Transactions
          </button>
        </div>

        {loading ? (
          <p className="p-5 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-5 text-sm text-muted-foreground">{emptyLabel ?? "No support transactions yet."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-5 py-3 font-semibold">Date</th>
                  <th className="px-5 py-3 font-semibold">Supporter</th>
                  {showEmail && <th className="px-5 py-3 font-semibold">Email</th>}
                  <th className="px-5 py-3 font-semibold">Transaction type</th>
                  <th className="px-5 py-3 font-semibold text-right">Gross amount</th>
                  <th className="px-5 py-3 font-semibold">Currency</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="px-5 py-3 whitespace-nowrap">{new Date(r.transaction_timestamp).toLocaleDateString()}</td>
                    <td className="px-5 py-3">{r.supporter_name || "Anonymous"}</td>
                    {showEmail && <td className="px-5 py-3 text-muted-foreground">{r.supporter_email || "—"}</td>}
                    <td className="px-5 py-3">{transactionTypeLabel(r.transaction_type)}</td>
                    <td className="px-5 py-3 text-right font-semibold tabular-nums">{toAmount(r.amount).toFixed(2)}</td>
                    <td className="px-5 py-3">{r.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsPanel;
